import { Injectable } from '@angular/core';
import { environment } from '@env/environment';
import { Observable } from 'rxjs';
import { resource } from '@util/resource';
import * as _ from 'lodash';
import { DWS } from "@interfaces/dws";

declare let gapi: any;
declare let window: any;

/** Result of Wine Suite Google login (GIS prompt / gapi fallback). */
export type WineSuiteGoogleLoginResult =
  | { ok: true; idToken: string }
  | { ok: false; reason: "cancelled" | "failed"; detail?: string };

@Injectable({
  providedIn: 'root'
})
export class GoogleAuthService {
  private ready: boolean = false;
  private url= 'https://www.googleapis.com/auth';
  private gisLoadPromise: Promise<void> | null = null;
  
  constructor() {
    window['gapiInit'] = () => {
      gapi.load('auth2', () => {
        this.ready = true;
        console.log('ready!');
      })
    };
    
    const scriptElement = document.createElement('script');
    scriptElement.defer = true;
    scriptElement.async = true;
    scriptElement.src = 'https://apis.google.com/js/platform.js?onload=gapiInit';
    document.body.append(scriptElement);
  }
  
  authorize(): Observable<any> {
    const authorize$ = new Observable<any>(observer => {
      gapi.auth2.authorize({
        client_id: environment.values['google-client-id'],
        scope: `${this.url}/userinfo.profile ${this.url}/userinfo.email ${this.url}/calendar ${this.url}/calendar.events ${this.url}/calendar.app.created`,
        response_type: 'code token id_token refresh_token',
        prompt: 'consent',
        access_type: 'offline',
        approval_prompt: 'force'
        // redirect_uri: 'http://localhost:3000/google-oauth2-callback'
      }, function(response: any, error: any) {
        console.log('response', response);
        console.log('error', error);
        observer.next(response);
        observer.complete();
      })
    });
    return authorize$;
  }

  /**
   * Authorize for login only - minimal scope (profile, email).
   * Kept as fallback when GIS One Tap / FedCM prompt cannot be displayed.
   */
  authorizeForLogin(): Observable<any> {
    const authorize$ = new Observable<any>(observer => {
      gapi.auth2.authorize({
        client_id: environment.values['google-client-id'],
        scope: `${this.url}/userinfo.profile ${this.url}/userinfo.email`,
        response_type: 'code token id_token refresh_token',
        prompt: 'consent',
        access_type: 'offline',
        approval_prompt: 'force'
      }, function(response: any, error: any) {
        observer.next(response);
        observer.complete();
      })
    });
    return authorize$;
  }

  /**
   * Custom-button Google sign-in for Wine Suite (single native Tab stop).
   * Uses GIS {@code prompt()} (One Tap / FedCM); falls back to gapi popup if prompt cannot show.
   * Returns the raw id_token for POST /account/authenticate-google.
   */
  loginForWineSuiteAccount(): Promise<WineSuiteGoogleLoginResult> {
    const clientId = environment.values['google-client-id'];
    if (!clientId) {
      console.warn('google-client-id not configured, Google sign-in will not run');
      return Promise.resolve({ ok: false, reason: 'failed', detail: 'not_configured' });
    }
    return this.ensureGisLoaded()
      .then(() => this.promptForCredential(clientId))
      .catch((err) => {
        console.warn('[GIS] prompt failed, falling back to gapi:', err);
        return this.authorizeForLoginFallback();
      });
  }

  private ensureGisLoaded(): Promise<void> {
    if ((window as any).google?.accounts?.id) {
      return Promise.resolve();
    }
    if (this.gisLoadPromise) {
      return this.gisLoadPromise;
    }
    this.gisLoadPromise = new Promise<void>((resolve, reject) => {
      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.onload = () => resolve();
      script.onerror = () => {
        this.gisLoadPromise = null;
        reject(new Error('Failed to load Google Identity Services script'));
      };
      document.head.appendChild(script);
    });
    return this.gisLoadPromise;
  }

  private promptForCredential(clientId: string): Promise<WineSuiteGoogleLoginResult> {
    return new Promise((resolve) => {
      let settled = false;
      const finish = (result: WineSuiteGoogleLoginResult) => {
        if (settled) {
          return;
        }
        settled = true;
        resolve(result);
      };

      (window as any).google.accounts.id.initialize({
        client_id: clientId,
        use_fedcm_for_prompt: true,
        callback: (response: { credential: string }) => {
          if (response?.credential) {
            finish({ ok: true, idToken: response.credential });
          } else {
            finish({ ok: false, reason: 'failed', detail: 'no_credential' });
          }
        }
      });

      (window as any).google.accounts.id.prompt((notification: {
        isNotDisplayed?: () => boolean;
        isSkippedMoment?: () => boolean;
        isDismissedMoment?: () => boolean;
        getNotDisplayedReason?: () => string;
        getDismissedReason?: () => string;
      }) => {
        if (notification.isNotDisplayed?.()) {
          const detail = notification.getNotDisplayedReason?.() || 'not_displayed';
          this.authorizeForLoginFallback().then((fallback) => {
            if (fallback.ok === false && fallback.reason === 'failed' && !fallback.detail) {
              finish({ ok: false, reason: 'failed', detail });
            } else {
              finish(fallback);
            }
          });
          return;
        }
        if (notification.isSkippedMoment?.()) {
          finish({ ok: false, reason: 'cancelled' });
          return;
        }
        if (notification.isDismissedMoment?.()) {
          const reason = notification.getDismissedReason?.();
          if (reason === 'credential_returned') {
            return;
          }
          finish({ ok: false, reason: 'cancelled' });
        }
      });
    });
  }

  private authorizeForLoginFallback(): Promise<WineSuiteGoogleLoginResult> {
    return new Promise((resolve) => {
      try {
        this.authorizeForLogin().subscribe({
          next: (response) => {
            if (response?.id_token) {
              resolve({ ok: true, idToken: response.id_token });
              return;
            }
            if (response?.error === 'popup_closed_by_user' || response?.error === 'access_denied') {
              resolve({ ok: false, reason: 'cancelled' });
              return;
            }
            if (response?.error) {
              resolve({ ok: false, reason: 'failed', detail: String(response.error) });
              return;
            }
            resolve({ ok: false, reason: 'cancelled' });
          },
          error: () => resolve({ ok: false, reason: 'failed' }),
        });
      } catch {
        resolve({ ok: false, reason: 'failed', detail: 'gapi_unavailable' });
      }
    });
  }

  list(filters?: GoogleAuthTokenFilters, page: number = 0, count: number = 999999): Observable<DWS.Page<DWS.GoogleAuthToken>> {
    return resource('crm://google-auth-tokens')
        .params(
            { page, count, ...filters }
        )
        .get<DWS.Page<DWS.GoogleAuthToken>>();
  }

  delete(id: string): Observable<void> {
    return resource('crm://google-auth-tokens/id')
        .params( { id })
        .delete()
  }

  link(credentials: any, wineryId: string) {
    return resource('crm://google-auth-tokens')
      .post(_.merge(credentials, { wineryId }));
  }
}

export interface GoogleAuthTokenFilters {
  wineryId: string;
}
